/**
 * TranscriptClientInfo (KAN-639): client company and linked cards in the
 * header of the transcript modal.
 *
 * Company comes from the client card (lead_info "Компания", any case). Each
 * link is a line "Связан: <name>, <role> (<company>)"; the name opens the
 * linked card's transcript in the same modal; every link has one
 * "Отменить связь" button (KAN-667: links are confirmed on creation).
 * Nothing is rendered without data.
 *
 * KAN-665: under the company a line of the client's contacts (phone, Telegram
 * username, e-mail) from the same client card; absent values are skipped.
 * KAN-833: the phone is a click-to-call button with the number as text.
 * KAN-877: one client, so call targets load at once (decision maker button).
 */
import { CompanyNameLink } from '@/components/clientCompanies/CompanyNameLink';
import { ManagerCallButton } from '@/components/clients/ManagerCallButton';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  linkedClientDetails,
  linkedClientName,
  useClientLinkList,
} from '@/components/clients/useClientLinkList';
import { useClient } from '@/services/api/clientQueries';
import type { Client, ClientLink, LinkedClientBrief } from '@/types/client';

interface TranscriptClientInfoProps {
  clientId: string;
  enabled: boolean;
  /** Open the linked card's transcript in the same modal. */
  onOpenClient: (client: LinkedClientBrief) => void;
  /** Linked client whose transcript is being opened, if any. */
  openingClientId?: string | null;
}

/** KAN-665: phone, Telegram username and e-mail of the client, known ones only. */
function ContactsLine({ client }: { client: Client }): JSX.Element | null {
  const username = (client.telegram_username ?? '').replace(/^@/, '');
  const items = [
    client.phone && (
      <ManagerCallButton
        key="phone"
        clientId={client.id}
        phone={client.phone}
        testId="transcript-client-call"
        targetMode="eager"
        className="text-xs"
      />
    ),
    username && (
      <a
        key="telegram"
        href={`https://t.me/${username}`}
        target="_blank"
        rel="noreferrer"
        className="hover:underline"
      >
        @{username}
      </a>
    ),
    client.email && (
      <a key="email" href={`mailto:${client.email}`} className="hover:underline">
        {client.email}
      </a>
    ),
  ].filter(Boolean);
  if (items.length === 0) return null;
  return (
    <p
      className="flex flex-wrap gap-x-3 gap-y-0.5 text-gray-700"
      data-testid="transcript-client-contacts"
    >
      {items}
    </p>
  );
}

function LinkLine({
  link,
  onOpenClient,
  opening,
  cancelling,
  onCancel,
}: {
  link: ClientLink;
  onOpenClient: (client: LinkedClientBrief) => void;
  opening: boolean;
  cancelling: boolean;
  /** Not passed: no cancel button (KAN-811: MANAGER role). */
  onCancel?: () => void;
}): JSX.Element {
  const { client } = link;
  const details = linkedClientDetails(client);
  return (
    <li
      className="flex flex-wrap items-center gap-x-2 gap-y-1"
      data-testid="transcript-linked-client"
    >
      <span className="text-gray-500">Связан:</span>
      <span className="text-gray-700">
        <button
          type="button"
          className="font-medium text-blue-700 hover:underline disabled:opacity-60"
          onClick={() => onOpenClient(client)}
          disabled={opening}
          title="Открыть переписку"
        >
          {linkedClientName(client)}
        </button>
        {details && <span data-testid="transcript-linked-client-details">{details}</span>}
      </span>
      {opening && <Loader2 className="h-3 w-3 animate-spin" />}
      {onCancel && (
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs text-gray-500"
          disabled={cancelling}
          onClick={onCancel}
        >
          Отменить связь
        </Button>
      )}
      {cancelling && <Loader2 className="h-3 w-3 animate-spin text-gray-400" />}
    </li>
  );
}

export function TranscriptClientInfo({
  clientId,
  enabled,
  onOpenClient,
  openingClientId = null,
}: TranscriptClientInfoProps): JSX.Element | null {
  const { data: client } = useClient(clientId, enabled);
  const { links: visible, decide, decidingLinkId, canDecide } = useClientLinkList(
    clientId,
    enabled
  );

  const company = client?.client_company?.name || client?.company || '';
  const hasContacts = !!(
    client &&
    (client.phone || client.telegram_username || client.email)
  );

  if (!company && !hasContacts && visible.length === 0) return null;

  return (
    <div className="space-y-1 text-xs" data-testid="transcript-client-info">
      {company && (
        <p className="text-gray-500" data-testid="transcript-client-company">
          <CompanyNameLink company={client?.client_company} fallback={company} />
        </p>
      )}
      {client && hasContacts && <ContactsLine client={client} />}
      {visible.length > 0 && (
        <ul className="space-y-1" data-testid="transcript-client-links">
          {visible.map((link) => (
            <LinkLine
              key={link.id}
              link={link}
              onOpenClient={onOpenClient}
              opening={openingClientId === link.client.id}
              cancelling={decidingLinkId === link.id}
              onCancel={canDecide ? () => decide(link.id, 'reject') : undefined}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
